import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchMyAssetRequests } from "services/asset-requests.service";
import { AssetRequest } from "types/AssetRequest";
import AssetRequestStatusChip from "./AssetRequestStatusChip";
import { formatRequestDay } from "./asset-requests-helpers";

/**
 * Profile → Turtas, below the employee's own assets: the list of their earlier requests
 * with how each ended. The page's main button files a new one and moves `reloadCount`.
 */
export default function SelfAssetRequests({ companyId, reloadCount }: { companyId: string; reloadCount: number }) {
  const { t } = useTranslation();
  // null until the first answer: an empty list must not flash «no requests» while loading.
  const [requests, setRequests] = useState<AssetRequest[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    fetchMyAssetRequests(companyId)
      .then((rows) => {
        if (!cancelled) setRequests(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, reloadCount]);

  const renderList = () => {
    if (loadFailed) return <p className="text-sm text-red-700">{t("asset_requests_load_failed")}</p>;
    if (requests === null) return <p className="text-sm text-gray-500">{t("loading")}</p>;
    if (requests.length === 0) return <p className="text-sm text-gray-500">{t("asset_requests_mine_empty")}</p>;
    return (
      <ul className="divide-y divide-gray-100">
        {requests.map((request) => (
          <li key={request.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2 text-sm">
            <AssetRequestStatusChip status={request.status} />
            <span className="text-gray-500">{formatRequestDay(request.createdAt)}</span>
            <div className="min-w-0 flex-1">
              {request.comment && <p className="whitespace-pre-line text-gray-900">{request.comment}</p>}
              {request.status === "rejected" && request.resolutionNote && (
                <p className="text-red-700">{t("asset_requests_rejected_reason", { reason: request.resolutionNote })}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-base font-semibold">{t("asset_requests_mine_title")}</h2>
      {renderList()}
    </section>
  );
}
